// ============================================================================
// marketWallet.ts — КОШЕЛЁК МАСТЕРА (витрина hydra.market).
//
// Только существующие RPC (без новых proto/полей/миграций):
//   • баланс/резервы/история — BillingService.GetMyBillingAccount
//     (личный счёт текущего пользователя; история — последние операции ядра);
//   • пополнение — PaymentService.ListPaymentProviders + CreatePayment
//     (тот же механизм, что «Мой счёт» и «Биллинг → Платежи»);
//   • номера заявок для истории — marketWalletHistory (mkt_requests).
//
// Деньги НЕ кэшируются и НЕ подменяются нулями: при обрыве связи/выключенном
// биллинге возвращается явная причина, а не выдуманный баланс. Офлайн-очередь
// для пополнения не используется — платёж без связи не создаётся.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import {
  friendlyError,
  isPermissionDeniedError,
  isTransportError,
  logErrorToJournal,
  logSwallowed,
} from '@/lib/errors';
import type { MasterBalance } from '@/lib/marketFeed';
import {
  buildWalletOperation,
  loadRequestHints,
  operationRequestId,
  sortWalletOperations,
  type MarketWalletOperation,
} from './marketWalletHistory';

export {
  WALLET_STATE_LABELS,
  type MarketWalletOperation,
  type MarketWalletOperationTone,
} from './marketWalletHistory';

export type MarketWalletFailureKind = 'disabled' | 'offline' | 'permission' | 'error';

export interface MarketWalletFailure {
  kind: MarketWalletFailureKind;
  message: string;
}

export type MarketWalletResult =
  | { ok: true; data: MarketWalletData }
  | ({ ok: false } & MarketWalletFailure);

export interface MarketWalletProvider {
  code: string;
  title: string;
  capabilities: string[];
}

export interface MarketWalletHold {
  id: string;
  amount: number;
  state: string;
  expiresAt: string;
}

export interface MarketWalletData {
  accountName: string;
  balance: MasterBalance;
  holds: MarketWalletHold[];
  operations: MarketWalletOperation[];
  providers: MarketWalletProvider[];
  /** Причина, по которой пополнение недоступно (пусто — доступно). */
  providersError: string;
  /** Время загрузки (ISO) — подпись «актуально на…». */
  loadedAt: string;
}

export interface MarketWalletTopUpSuccess {
  ok: true;
  status: string;
  confirmationUrl: string;
  amountMinor: number;
  paid: boolean;
}

export interface MarketWalletTopUpFailure {
  ok: false;
  message: string;
}

export type MarketWalletTopUpResult = MarketWalletTopUpSuccess | MarketWalletTopUpFailure;

export const WALLET_HOLD_STATE_LABELS: Record<string, string> = {
  active: 'Активен',
  captured: 'Списан',
  released: 'Снят',
};

function isBillingDisabledMessage(message: string): boolean {
  const s = message.toLowerCase();
  return (
    s.includes('биллинг не включён')
    || s.includes('внутренние операции выключены')
    || s.includes('money=false')
    || s.includes('денежный контур')
  );
}

function holdFromProto(raw: any): MarketWalletHold {
  return {
    id: String(raw?.id || ''),
    amount: Number(raw?.amount || 0) || 0,
    state: String(raw?.state || ''),
    expiresAt: String(raw?.expiresAt || ''),
  };
}

/** Загрузка провайдеров: сбой не ломает кошелёк, но пополнение объясняется текстом. */
async function loadProvidersSafe(): Promise<{ items: MarketWalletProvider[]; error: string }> {
  try {
    const res: any = await getSDK().payments.listPaymentProviders({});
    const items = ((res?.items || []) as any[])
      .filter((p) => p && p.enabled !== false)
      .map((p) => ({
        code: String(p.code || ''),
        title: String(p.title || p.code || 'Платёжный провайдер'),
        capabilities: ((p.capabilities || []) as unknown[]).map((c) => String(c)).filter(Boolean),
      }))
      .filter((p) => p.code);
    return { items, error: '' };
  } catch (e) {
    logSwallowed(e, 'Кошелёк мастера: платёжные провайдеры');
    return { items: [], error: friendlyError(e).trim() || 'Не удалось получить способы пополнения' };
  }
}

/** Сводка по личному счёту мастера: баланс, резервы, история, способы пополнения. */
export async function loadMarketWallet(userId: string): Promise<MarketWalletResult> {
  if (!userId) {
    return {
      ok: false,
      kind: 'permission',
      message: 'Не определён текущий пользователь — войдите заново',
    };
  }
  if (isEffectivelyOffline()) {
    return {
      ok: false,
      kind: 'offline',
      message: 'Нет связи с сервером — баланс и история недоступны до восстановления связи',
    };
  }
  try {
    const res: any = await getSDK().billing.getMyBillingAccount({});
    const balance = res?.balance;
    if (!balance) {
      return { ok: false, kind: 'error', message: 'Сервер не вернул баланс личного счёта' };
    }
    const rawOperations = (res.operations || []) as any[];
    const requestIds = new Set<string>();
    for (const op of rawOperations) {
      const id = operationRequestId(op);
      if (id) requestIds.add(id);
    }
    const [providers, hints] = await Promise.all([
      loadProvidersSafe(),
      loadRequestHints(userId, [...requestIds]),
    ]);
    const providerTitles = new Map(providers.items.map((p) => [p.code, p.title]));
    const data: MarketWalletData = {
      accountName: String(res.account?.name || 'Личный счёт'),
      balance: {
        balance: Number(balance.balance || 0),
        holds: Number(balance.holds || 0),
        available: Number(balance.available || 0),
        currency: String(balance.currency || ''),
      },
      holds: ((res.holds || []) as any[]).map(holdFromProto).filter((h) => h.id),
      operations: sortWalletOperations(
        rawOperations.map((op) => buildWalletOperation(op, hints, providerTitles)),
      ),
      providers: providers.items,
      providersError: providers.error,
      loadedAt: new Date().toISOString(),
    };
    return { ok: true, data };
  } catch (e) {
    const message = friendlyError(e).trim() || 'Не удалось загрузить кошелёк';
    if (isTransportError(e)) {
      return { ok: false, kind: 'offline', message };
    }
    if (isPermissionDeniedError(e)) {
      return { ok: false, kind: 'permission', message };
    }
    if (isBillingDisabledMessage(message)) {
      return { ok: false, kind: 'disabled', message };
    }
    return { ok: false, kind: 'error', message };
  }
}

/** Сумма в рублях → минорные единицы платёжного слоя (копейки). */
export function rubToMinor(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value * 100);
}

function topUpClientRef(userId: string): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `market-topup:${userId}:${Date.now()}:${random}`;
}

/**
 * Создать платёж пополнения. Ошибки — явным текстом; офлайн — отказ до сети.
 * Сумма вводится в рублях, платёжный слой принимает копейки (как «Мой счёт»).
 */
export async function createMarketWalletTopUp(
  provider: string,
  amountRub: number,
  userId: string,
): Promise<MarketWalletTopUpResult> {
  const amountMinor = rubToMinor(amountRub);
  if (!provider) return { ok: false, message: 'Выберите способ пополнения' };
  if (!amountMinor) return { ok: false, message: 'Укажите сумму пополнения больше нуля' };
  if (isEffectivelyOffline()) {
    return {
      ok: false,
      message: 'Пополнение требует связи с сервером — восстановите подключение и повторите',
    };
  }
  if (!userId) return { ok: false, message: 'Не определён текущий пользователь — войдите заново' };
  try {
    const res: any = await getSDK().payments.createPayment({
      provider,
      amount: BigInt(amountMinor),
      currency: 'RUB',
      purpose: 'topup',
      description: 'Пополнение счёта мастера',
      clientRef: topUpClientRef(userId),
    });
    const status = String(res?.status || '');
    return {
      ok: true,
      status,
      confirmationUrl: String(res?.confirmationUrl || ''),
      amountMinor,
      paid: status === 'paid' || status === 'succeeded',
    };
  } catch (e) {
    const message = friendlyError(e).trim() || 'Не удалось создать платёж';
    logErrorToJournal(e, 'Кошелёк мастера: создание платежа');
    return { ok: false, message };
  }
}
